/* =====================================================================
 * CloudDriveModal — Google Drive (Picker) / OneDrive 파일 선택 모달
 *
 * Google Drive: drive.file 스코프 + Google Picker API 기반.
 *   - 플러그인 페이지 대신 이 모달에서 인라인 OAuth 연결.
 *   - Picker로 파일 선택 → 다운로드 URL로 첨부.
 * OneDrive: 기존 폴더 탐색 방식 유지.
 * ===================================================================== */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import {
  Folder,
  FileText,
  FileSpreadsheet,
  Image,
  FileIcon,
  Presentation,
  ChevronRight,
  Search,
  X,
  ArrowLeft,
  RefreshCw,
  Cloud,
  Loader,
  HardDrive,
  LogOut,
} from 'lucide-react';

import { Modal } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { SkeletonLine } from '@auw/workspace-ui';
import { Tabs, type TabItem } from '@auw/workspace-ui';
import { RingSpinner } from '@auw/workspace-ui';
import {
  listPlugins,
  listOneDriveFiles,
  searchOneDriveFiles,
  getCloudDriveDownloadUrl,
  getGoogleDriveOAuthUrl,
  getGoogleDrivePickerConfig,
  getOneDriveOAuthUrl,
  disconnectGoogleDrive,
  disconnectOneDrive,
} from '@auw/api-client';
import type { DriveItem, PluginInfo } from '@auw/api-client';
import type { FileAttachment } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';
import { queryKeys } from '@auw/workspace-utils';
import {
  CHAT_ATTACHMENT_MAX_COUNT,
  MODAL_GC_TIME,
  MODAL_STALE_TIME,
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  getRemainingAttachmentSlots,
  isAttachmentSizeAllowed,
} from './attachmentConstraints';
import { useTranslation } from 'react-i18next';
import { getTenantSnapshot } from '@auw/onprem';
import {
  buildPluginOAuthRedirectUri,
  clearPendingPluginOAuthFlow,
  createPluginOAuthFlowId,
  setPendingPluginOAuthFlow,
  getPluginOAuthChannel,
  getPluginOAuthPopupName,
  type PluginOAuthResultMessage,
} from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Google API loader (on-demand)                                       */
/*                                                                     */
/* index.html에서 <script src="apis.google.com/js/api.js">를 제거했기   */
/* 때문에, Google Drive Picker가 실제로 필요해질 때 이 헬퍼가 loader를  */
/* document.head에 주입한다. 같은 페이지에서 여러 번 호출되어도 실제    */
/* 네트워크 요청은 한 번만 발생하도록 프라미스를 캐시한다. 초기 HTML에  */
/* 외부 <script>가 없어야 TAC ESOF (CASA Tier 2/3) 스캔의 SRI 누락 /    */
/* Cross-Domain JS 탐지를 회피할 수 있다.                              */
/*                                                                     */
/* Phase 8 — 테넌트가 플러그인 전체를 disabled 처리했거나              */
/* `google_drive`를 명시적으로 disabled 처리한 경우 loader 는 throw 해  */
/* 외부 스크립트가 절대 주입되지 않도록 방어한다. `hiddenIds`는          */
/* 플러그인 카탈로그 숨김 용도이므로 + 메뉴의 클라우드 첨부 흐름을       */
/* 막지 않는다.                                                        */
/* ------------------------------------------------------------------ */

const GAPI_SCRIPT_SRC = 'https://apis.google.com/js/api.js';
let gapiLoaderPromise: Promise<void> | null = null;

function assertGoogleDrivePluginEnabled(): void {
  const tenant = getTenantSnapshot();
  if (!tenant) return;
  const plugins = tenant.plugins;
  if (!plugins) return;
  if (plugins.enabled === false) {
    throw new Error('플러그인이 이 테넌트에서 비활성화되어 있습니다.');
  }
  const override = plugins.overrides?.google_drive;
  if (override?.hidden === true) {
    throw new Error('Google Drive 플러그인이 이 테넌트에서 비활성화되어 있습니다.');
  }
}

function ensureGapi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  try {
    assertGoogleDrivePluginEnabled();
  } catch (err) {
    return Promise.reject(err);
  }
  if ((window as { gapi?: unknown }).gapi) return Promise.resolve();
  if (gapiLoaderPromise) return gapiLoaderPromise;

  gapiLoaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GAPI_SCRIPT_SRC}"]`,
    );
    if (existing) {
      if ((window as { gapi?: unknown }).gapi) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => {
          gapiLoaderPromise = null;
          reject(new Error('Google API 스크립트 로드에 실패했습니다.'));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = GAPI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gapiLoaderPromise = null;
      reject(new Error('Google API 스크립트 로드에 실패했습니다.'));
    };
    document.head.appendChild(script);
  });

  return gapiLoaderPromise;
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type DriveProvider = 'google_drive' | 'onedrive';

interface CloudDriveModalProps {
  open: boolean;
  onClose: () => void;
  initialTab: DriveProvider;
  onSelect: (files: FileAttachment[]) => void;
  existingAttachmentCount?: number;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getFileIcon(item: DriveItem) {
  const mime = item.mimeType || '';
  if (item.isFolder || item.type === 'folder') return <Folder size={18} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return <FileSpreadsheet size={18} />;
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return <Presentation size={18} />;
  if (mime.startsWith('image/')) return <Image size={18} />;
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('word') || mime.includes('text'))
    return <FileText size={18} />;
  return <FileIcon size={18} />;
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function isFolder(item: DriveItem): boolean {
  return item.isFolder === true || item.type === 'folder';
}

const ALLOWED_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg',
  'pdf', 'doc', 'docx', 'hwp', 'hwpx', 'xls', 'xlsx', 'csv', 'txt', 'md',
  'pptx', 'ppt',
  'mp3', 'wav', 'mp4', 'webm', 'ogg',
  'json', 'xml', 'yaml', 'yml', 'py', 'js', 'ts', 'html', 'css',
  'zip', 'tar', 'gz',
]);

const GOOGLE_APPS_EXPORTABLE: Record<string, { ext: string; exportName: string }> = {
  'application/vnd.google-apps.document':     { ext: 'pdf',  exportName: 'PDF' },
  'application/vnd.google-apps.spreadsheet':  { ext: 'xlsx', exportName: 'Excel' },
  'application/vnd.google-apps.presentation': { ext: 'pdf',  exportName: 'PDF' },
  'application/vnd.google-apps.drawing':      { ext: 'png',  exportName: 'PNG' },
};

function isSelectableFile(item: DriveItem): boolean {
  if (isFolder(item)) return true;
  const mime = item.mimeType || '';
  if (mime.startsWith('application/vnd.google-apps.')) {
    return mime in GOOGLE_APPS_EXPORTABLE;
  }
  const ext = item.name.split('.').pop()?.toLowerCase() || '';
  return ALLOWED_EXTENSIONS.has(ext);
}

function getExportInfo(item: DriveItem): { ext: string; exportName: string } | null {
  const mime = item.mimeType || '';
  return GOOGLE_APPS_EXPORTABLE[mime] ?? null;
}

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const CloudDriveModal: React.FC<CloudDriveModalProps> = ({
  open,
  onClose,
  initialTab,
  onSelect,
  existingAttachmentCount = 0,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
}) => {
  const addToast = useChatStore((s) => s.addToast);
  const { t } = useTranslation('widget');

  const formatDateLocalized = useCallback((dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return t('cloudDrive.today');
      if (diffDays === 1) return t('cloudDrive.yesterday');
      if (diffDays < 7) return t('cloudDrive.daysAgo', { count: diffDays });
      return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }, [t]);

  /* ---- Tab state ---- */
  const [activeTab, setActiveTab] = useState<DriveProvider>(initialTab);

  const {
    data: pluginsData,
    isFetching: pluginsFetching,
    refetch: refetchPlugins,
  } = useQuery<PluginInfo[]>({
    queryKey: queryKeys.plugins.list(),
    queryFn: async () => {
      const resp = await listPlugins();
      return resp.plugins;
    },
    enabled: open,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: true,
  });
  const plugins = pluginsData ?? [];
  const pluginsLoading = pluginsFetching && !pluginsData;

  /* ---- OneDrive file browser state ---- */
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, DriveItem>>(new Map());
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  /* ---- Google Drive: Picker로 선택된 파일 ---- */
  const [pickedFiles, setPickedFiles] = useState<DriveItem[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [oauthConnecting, setOauthConnecting] = useState(false);

  /* ---- OneDrive: folder stack by path/id ---- */
  const [onedriveBreadcrumb, setOnedriveBreadcrumb] = useState<BreadcrumbItem[]>([
    { id: '', name: t('cloudDrive.myDrive') },
  ]);

  /* ---- OneDrive Search state ---- */
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- Sync initialTab ---- */
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setSelected(new Map());
      setPickedFiles([]);
      setSearchQuery('');
      setIsSearchMode(false);
    }
  }, [open, initialTab]);

  /* ---- Tab config ---- */
  const tabItems: TabItem[] = [
    { id: 'google_drive', label: 'Google Drive', icon: <TabIcon src="/logo/google_drive.png" alt="Google Drive" /> },
    { id: 'onedrive', label: 'OneDrive', icon: <TabIcon src="/logo/onedrive.png" alt="OneDrive" /> },
  ];

  useEffect(() => {
    if (open) void refetchPlugins();
  }, [open, refetchPlugins]);

  const isConnected = (provider: DriveProvider): boolean =>
    plugins.some((p) => p.id === provider && p.connected);

  /* ==================================================================
   * Google Drive: 인라인 OAuth 연결
   * ================================================================== */
  const handleGoogleDriveConnect = useCallback(async () => {
    setOauthConnecting(true);
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},popup=1`;
    const popup = window.open('', '_blank', features);
    const flowId = createPluginOAuthFlowId();

    setPendingPluginOAuthFlow({
      plugin: 'google_drive',
      flowId,
      popupExpected: Boolean(popup),
      startedAt: Date.now(),
    });

    if (popup) {
      popup.name = getPluginOAuthPopupName();
      try {
        popup.document.body.innerHTML =
          '<div style="font-family:system-ui;padding:20px;text-align:center;color:#444;">Google 인증 페이지로 이동 중...</div>';
      } catch { /* cross-origin */ }
    }

    try {
      const redirectUri = buildPluginOAuthRedirectUri(
        `${window.location.origin}${window.location.pathname}`,
        { flowId, popupExpected: Boolean(popup) },
      );
      const result = await getGoogleDriveOAuthUrl(redirectUri);

      if (popup && !popup.closed) {
        popup.location.href = result.authorization_url;

        const pollId = window.setInterval(() => {
          if (popup.closed) {
            clearInterval(pollId);
            clearPendingPluginOAuthFlow(flowId);
            setOauthConnecting(false);
            void refetchPlugins();
          }
        }, 1000);

        window.setTimeout(() => {
          clearInterval(pollId);
          if (!popup.closed) popup.close();
          clearPendingPluginOAuthFlow(flowId);
          setOauthConnecting(false);
        }, 5 * 60 * 1000);
      } else {
        window.location.href = result.authorization_url;
      }
    } catch (e: unknown) {
      if (popup && !popup.closed) popup.close();
      clearPendingPluginOAuthFlow(flowId);
      setOauthConnecting(false);
      addToast({
        type: 'error',
        title: 'Google Drive 연결 실패',
        description: e instanceof Error ? e.message : 'OAuth URL 생성에 실패했습니다.',
      });
    }
  }, [addToast, refetchPlugins]);

  // OAuth 팝업 결과 수신
  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    const handler = (msg: PluginOAuthResultMessage) => {
      if (msg.plugin !== 'google_drive') return;
      setOauthConnecting(false);
      void refetchPlugins();
      if (msg.status === 'connected') {
        addToast({ type: 'success', title: 'Google Drive 연결 완료', duration: 2000 });
      }
    };
    try {
      ch = new BroadcastChannel(getPluginOAuthChannel());
      ch.addEventListener('message', (e: MessageEvent<PluginOAuthResultMessage>) => handler(e.data));
    } catch { /* fallback */ }
    return () => { ch?.close(); };
  }, [addToast, refetchPlugins]);

  /* ==================================================================
   * OneDrive: 인라인 OAuth 연결
   * ================================================================== */
  const handleOneDriveConnect = useCallback(async () => {
    setOauthConnecting(true);
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},popup=1`;
    const popup = window.open('', '_blank', features);
    const flowId = createPluginOAuthFlowId();

    setPendingPluginOAuthFlow({
      plugin: 'onedrive',
      flowId,
      popupExpected: Boolean(popup),
      startedAt: Date.now(),
    });

    if (popup) {
      popup.name = getPluginOAuthPopupName();
      try {
        popup.document.body.innerHTML =
          '<div style="font-family:system-ui;padding:20px;text-align:center;color:#444;">Microsoft 인증 페이지로 이동 중...</div>';
      } catch { /* cross-origin */ }
    }

    try {
      const redirectUri = buildPluginOAuthRedirectUri(
        `${window.location.origin}${window.location.pathname}`,
        { flowId, popupExpected: Boolean(popup) },
      );
      const result = await getOneDriveOAuthUrl(redirectUri);

      if (popup && !popup.closed) {
        popup.location.href = result.authorization_url;

        const pollId = window.setInterval(() => {
          if (popup.closed) {
            clearInterval(pollId);
            clearPendingPluginOAuthFlow(flowId);
            setOauthConnecting(false);
            void refetchPlugins();
          }
        }, 1000);

        window.setTimeout(() => {
          clearInterval(pollId);
          if (!popup.closed) popup.close();
          clearPendingPluginOAuthFlow(flowId);
          setOauthConnecting(false);
        }, 5 * 60 * 1000);
      } else {
        window.location.href = result.authorization_url;
      }
    } catch (e: unknown) {
      if (popup && !popup.closed) popup.close();
      clearPendingPluginOAuthFlow(flowId);
      setOauthConnecting(false);
      addToast({
        type: 'error',
        title: 'OneDrive 연결 실패',
        description: e instanceof Error ? e.message : 'OAuth URL 생성에 실패했습니다.',
      });
    }
  }, [addToast, refetchPlugins]);

  // OneDrive OAuth 팝업 결과 수신
  useEffect(() => {
    let ch: BroadcastChannel | null = null;
    const handler = (msg: PluginOAuthResultMessage) => {
      if (msg.plugin !== 'onedrive') return;
      setOauthConnecting(false);
      void refetchPlugins();
      if (msg.status === 'connected') {
        addToast({ type: 'success', title: 'OneDrive 연결 완료', duration: 2000 });
      }
    };
    try {
      ch = new BroadcastChannel(getPluginOAuthChannel());
      ch.addEventListener('message', (e: MessageEvent<PluginOAuthResultMessage>) => handler(e.data));
    } catch { /* fallback */ }
    return () => { ch?.close(); };
  }, [addToast, refetchPlugins]);

  /* ==================================================================
   * 연결 해제 (공통)
   * ================================================================== */
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = useCallback(async (provider: DriveProvider) => {
    setDisconnecting(true);
    try {
      if (provider === 'google_drive') {
        await disconnectGoogleDrive();
      } else {
        await disconnectOneDrive();
      }
      addToast({ type: 'success', title: `${provider === 'google_drive' ? 'Google Drive' : 'OneDrive'} 연결 해제 완료`, duration: 2000 });
      setPickedFiles([]);
      setSelected(new Map());
      setItems([]);
      void refetchPlugins();
    } catch (e: unknown) {
      addToast({
        type: 'error',
        title: '연결 해제 실패',
        description: e instanceof Error ? e.message : '다시 시도해주세요.',
      });
    } finally {
      setDisconnecting(false);
    }
  }, [addToast, refetchPlugins]);

  /* ==================================================================
   * Google Drive: Picker 열기
   * ================================================================== */
  const openGooglePicker = useCallback(async () => {
    setPickerLoading(true);

    const sidebarOffset = Math.round((modalCenterPaddingLeft || 0) / 2);
    const pickerStyle = document.createElement('style');
    const rules: string[] = [];
    if (sidebarOffset > 0) {
      rules.push(`.picker-dialog { margin-left: ${sidebarOffset}px !important; }`);
    }
    const pickerWidth = Math.min(window.innerWidth - (modalCenterPaddingLeft || 0) - 40, 1024);
    rules.push(
      `.picker-dialog { max-width: none !important; width: ${pickerWidth}px !important; }`,
      `.picker-dialog-content { width: 100% !important; max-width: none !important; }`,
      `.picker-dialog iframe { width: 100% !important; min-width: 100% !important; }`,
    );
    pickerStyle.textContent = rules.join('\n');
    document.head.appendChild(pickerStyle);

    try {
      const config = await getGoogleDrivePickerConfig();

      await ensureGapi();

      await new Promise<void>((resolve, reject) => {
        if ((window as any).google?.picker) { resolve(); return; }
        const gapi = (window as any).gapi;
        if (!gapi) { reject(new Error('Google API 스크립트가 로드되지 않았습니다.')); return; }
        gapi.load('picker', { callback: resolve, onerror: reject });
      });

      const google = (window as any).google;

      const myDriveView = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setOwnedByMe(true)
        .setMode(google.picker.DocsViewMode.LIST);

      const sharedDriveView = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setEnableDrives(true)
        .setOwnedByMe(false)
        .setMode(google.picker.DocsViewMode.LIST);

      const sharedWithMeView = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setOwnedByMe(false)
        .setMode(google.picker.DocsViewMode.LIST);

      const origin = window.location.protocol + '//' + window.location.host;

      const picker = new google.picker.PickerBuilder()
        .setDeveloperKey(config.api_key)
        .setAppId(config.app_id)
        .setOAuthToken(config.oauth_token)
        .setOrigin(origin)
        .addView(myDriveView)
        .addView(sharedDriveView)
        .addView(sharedWithMeView)
        .addView(new google.picker.DocsUploadView())
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
        .setCallback((data: any) => {
          if (data.action === google.picker.Action.PICKED) {
            const allFiles: DriveItem[] = data.docs.map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              isFolder: false,
              size: doc.sizeBytes ?? null,
              modifiedTime: doc.lastEditedUtc ? new Date(doc.lastEditedUtc).toISOString() : undefined,
            }));
            const supported = allFiles.filter((f) => isSelectableFile(f));
            const unsupported = allFiles.filter((f) => !isSelectableFile(f));

            if (unsupported.length > 0) {
              const names = unsupported.map((f) => f.name).join(', ');
              addToast({
                type: 'warning',
                title: t('cloudDrive.unsupportedFileType'),
                description: `${names} — 지원하지 않는 파일 형식입니다.`,
              });
            }

            if (supported.length > 0) {
              setPickedFiles((prev) => {
                const existing = new Map(prev.map((f) => [f.id, f]));
                for (const f of supported) existing.set(f.id, f);
                return Array.from(existing.values());
              });
              for (const f of supported) {
                setSelected((prev) => {
                  const next = new Map(prev);
                  if (!next.has(f.id)) next.set(f.id, f);
                  return next;
                });
              }
            }
          }
          if (data.action === google.picker.Action.PICKED || data.action === google.picker.Action.CANCEL) {
            pickerStyle?.remove();
          }
        })
        .setSize(1024, 650)
        .build();

      picker.setVisible(true);
    } catch (e: unknown) {
      pickerStyle?.remove();
      addToast({
        type: 'error',
        title: t('cloudDrive.errorOccurred'),
        description: e instanceof Error ? e.message : 'Picker를 열 수 없습니다.',
      });
    } finally {
      setPickerLoading(false);
    }
  }, [addToast, t, modalCenterPaddingLeft]);

  /* ==================================================================
   * OneDrive: 기존 폴더 탐색 방식 유지
   * ================================================================== */
  const fetchFiles = useCallback(async (
    folderId?: string,
    pageToken?: string,
  ) => {
    const isAppend = !!pageToken;
    if (isAppend) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setNextPageToken(null);
    }
    setError(null);
    try {
      const resp = await listOneDriveFiles('', folderId || '', 100, pageToken || undefined);
      const sortItems = (list: DriveItem[]) =>
        [...list].sort((a, b) => {
          const af = isFolder(a) ? 0 : 1;
          const bf = isFolder(b) ? 0 : 1;
          if (af !== bf) return af - bf;
          return a.name.localeCompare(b.name);
        });

      if (isAppend) {
        setItems((prev) => [...prev, ...sortItems(resp.items)]);
      } else {
        setItems(sortItems(resp.items));
      }
      setNextPageToken(resp.nextPageToken ?? null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('cloudDrive.fileListError');
      if (!isAppend) {
        setError(msg);
        setItems([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  const searchFiles = useCallback(async (
    query: string,
    pageToken?: string,
  ) => {
    if (!query.trim()) return;
    const isAppend = !!pageToken;
    if (isAppend) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setNextPageToken(null);
    }
    setError(null);
    setIsSearchMode(true);
    try {
      const resp = await searchOneDriveFiles(query, 50, pageToken || undefined);
      if (isAppend) {
        setItems((prev) => [...prev, ...resp.items]);
      } else {
        setItems(resp.items);
      }
      setNextPageToken(resp.nextPageToken ?? null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('cloudDrive.searchFailed');
      if (!isAppend) {
        setError(msg);
        setItems([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  /* ---- Load OneDrive on tab change / open / connection status change ---- */
  const onedriveConnected = isConnected('onedrive');
  useEffect(() => {
    if (!open || pluginsLoading) return;
    if (activeTab !== 'onedrive' || !onedriveConnected) return;
    setSelected(new Map());
    setSearchQuery('');
    setIsSearchMode(false);
    setNextPageToken(null);
    const currentFolder = onedriveBreadcrumb[onedriveBreadcrumb.length - 1].id;
    fetchFiles(currentFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, pluginsLoading, onedriveConnected]);

  /* ---- OneDrive Infinite scroll ---- */
  const nextPageTokenRef = useRef(nextPageToken);
  nextPageTokenRef.current = nextPageToken;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;

  useEffect(() => {
    if (activeTab !== 'onedrive') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          nextPageTokenRef.current &&
          !loadingRef.current &&
          !loadingMoreRef.current
        ) {
          const currentFolder = onedriveBreadcrumb[onedriveBreadcrumb.length - 1].id;
          if (isSearchMode) {
            searchFiles(searchQuery, nextPageTokenRef.current);
          } else {
            fetchFiles(currentFolder, nextPageTokenRef.current);
          }
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSearchMode, searchQuery, fetchFiles, searchFiles, onedriveBreadcrumb]);

  /* ---- OneDrive Folder navigation ---- */
  const handleFolderClick = (item: DriveItem) => {
    if (!isFolder(item)) return;
    setSearchQuery('');
    setIsSearchMode(false);
    setSelected(new Map());
    setNextPageToken(null);
    setOnedriveBreadcrumb((prev) => [...prev, { id: item.id, name: item.name }]);
    fetchFiles(item.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    setSearchQuery('');
    setIsSearchMode(false);
    setSelected(new Map());
    setNextPageToken(null);
    const newBreadcrumb = onedriveBreadcrumb.slice(0, index + 1);
    setOnedriveBreadcrumb(newBreadcrumb);
    fetchFiles(newBreadcrumb[newBreadcrumb.length - 1].id);
  };

  const handleGoBack = () => {
    if (onedriveBreadcrumb.length <= 1) return;
    handleBreadcrumbClick(onedriveBreadcrumb.length - 2);
  };

  /* ---- OneDrive Search ---- */
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setNextPageToken(null);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!value.trim()) {
      setIsSearchMode(false);
      fetchFiles(onedriveBreadcrumb[onedriveBreadcrumb.length - 1].id);
      return;
    }

    searchTimerRef.current = setTimeout(() => {
      searchFiles(value);
    }, 300);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearchMode(false);
    setNextPageToken(null);
    fetchFiles(onedriveBreadcrumb[onedriveBreadcrumb.length - 1].id);
  };

  /* ---- Selection (공통) ---- */
  const unsupportedToastShownRef = useRef(false);

  const toggleSelect = (item: DriveItem) => {
    if (isFolder(item)) return;
    if (!isSelectableFile(item)) {
      if (!unsupportedToastShownRef.current) {
        unsupportedToastShownRef.current = true;
        addToast({
          type: 'warning',
          title: t('cloudDrive.unsupportedFileType'),
          description: t('cloudDrive.unsupportedFileTypeDesc', { name: item.name }),
        });
        setTimeout(() => { unsupportedToastShownRef.current = false; }, 2000);
      }
      return;
    }
    if (!isAttachmentSizeAllowed(item.size)) {
      const toast = buildAttachmentSizeLimitToast([{ name: item.name, size: item.size }]);
      addToast({ type: 'warning', title: toast.title, description: toast.description });
      return;
    }
    if (!selected.has(item.id) && getRemainingAttachmentSlots(existingAttachmentCount, selected.size) === 0) {
      const toast = buildAttachmentCountLimitToast();
      addToast({ type: 'warning', title: toast.title, description: toast.description });
      return;
    }
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  };

  /* ---- Submit (공통) ---- */
  const handleAttach = () => {
    const attachments: FileAttachment[] = Array.from(selected.values()).map((item) => {
      const exportInfo = getExportInfo(item);
      const name = exportInfo
        ? `${item.name.replace(/\.[^.]+$/, '')}.${exportInfo.ext}`
        : item.name;
      const mimeMap: Record<string, string> = {
        pdf: 'application/pdf',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      const type = exportInfo
        ? mimeMap[exportInfo.ext] || 'application/octet-stream'
        : item.mimeType || 'application/octet-stream';

      return {
        id: uuid(),
        name,
        type,
        size: item.size ?? 0,
        cloudSource: activeTab,
        driveFileId: item.id,
        remoteUrl: getCloudDriveDownloadUrl(activeTab, item.id),
      };
    });
    onSelect(attachments);
    onClose();
  };

  /* ---- Render: Google Drive — not connected ---- */
  const renderGdriveNotConnected = () => (
    <NotConnectedArea>
      <EmptyState
        icon={
          <DriveLogoWrap>
            <img src="/logo/google_drive.png" alt="Google Drive" width={48} height={48} />
          </DriveLogoWrap>
        }
        title={t('cloudDrive.notConnected', { provider: 'Google Drive' })}
        action={
          <Button
            variant="primary"
            size="md"
            onClick={handleGoogleDriveConnect}
            disabled={oauthConnecting}
            isLoading={oauthConnecting}
          >
            {oauthConnecting ? t('cloudDrive.connecting') || '연결 중...' : t('cloudDrive.connect')}
          </Button>
        }
      />
    </NotConnectedArea>
  );

  /* ---- Render: Google Drive — connected (Picker 기반) ---- */
  const renderGdrivePicker = () => (
    <BrowserArea>
      <ConnectionBar>
        <ConnectionInfo>
          <ConnectionDot />
          <ConnectionLabel>Google Drive 연결됨</ConnectionLabel>
        </ConnectionInfo>
        <DisconnectBtn onClick={() => handleDisconnect('google_drive')} disabled={disconnecting}>
          <LogOut size={13} />
          <span>{disconnecting ? '해제 중...' : '연결 해제'}</span>
        </DisconnectBtn>
      </ConnectionBar>
      <PickerButtonArea>
        <PickerButton onClick={openGooglePicker} disabled={pickerLoading}>
          {pickerLoading ? (
            <RingSpinner size={20} />
          ) : (
            <HardDrive size={20} />
          )}
          <PickerButtonText>
            <PickerButtonTitle>Google Drive에서 파일 선택</PickerButtonTitle>
            <PickerButtonDesc>클릭하면 Google 파일 선택 창이 열립니다</PickerButtonDesc>
          </PickerButtonText>
        </PickerButton>
      </PickerButtonArea>

      {pickedFiles.length > 0 && (
        <>
          <PickerDivider>
            <PickerDividerLine />
            <PickerDividerLabel>선택된 파일</PickerDividerLabel>
            <PickerDividerLine />
          </PickerDivider>
          <ScrollArea ref={scrollAreaRef}>
            <FileListArea>
              {pickedFiles.map((item) => {
                const isSelected = selected.has(item.id);
                return (
                  <FileRow
                    key={item.id}
                    $isFolder={false}
                    $isSelected={isSelected}
                    onClick={() => toggleSelect(item)}
                  >
                    <CheckboxCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onChange={() => toggleSelect(item)} size="sm" />
                    </CheckboxCell>
                    <FileIconWrap $isFolder={false}>{getFileIcon(item)}</FileIconWrap>
                    <FileInfo>
                      <FileName>
                        {item.name}
                        {getExportInfo(item) && (
                          <ExportBadge>{t('cloudDrive.convertTo', { format: getExportInfo(item)!.exportName })}</ExportBadge>
                        )}
                      </FileName>
                      <FileMeta>{formatFileSize(item.size)}</FileMeta>
                    </FileInfo>
                    <RemovePickedBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickedFiles((prev) => prev.filter((f) => f.id !== item.id));
                        setSelected((prev) => { const next = new Map(prev); next.delete(item.id); return next; });
                      }}
                    >
                      <X size={14} />
                    </RemovePickedBtn>
                  </FileRow>
                );
              })}
            </FileListArea>
          </ScrollArea>
        </>
      )}

      {pickedFiles.length === 0 && (
        <EmptyPickerArea>
          <EmptyState
            icon={<Cloud size={32} />}
            title={t('cloudDrive.selectFiles')}
            description="위 버튼을 클릭하여 Google Drive에서 파일을 선택하세요"
          />
        </EmptyPickerArea>
      )}

      <ActionBar>
        <SelectedCount>
          {selected.size > 0
            ? t('cloudDrive.filesSelected', { count: selected.size, total: existingAttachmentCount + selected.size, max: CHAT_ATTACHMENT_MAX_COUNT })
            : t('cloudDrive.selectFiles')}
        </SelectedCount>
        <Button variant="primary" size="sm" disabled={selected.size === 0} onClick={handleAttach}>
          {t('cloudDrive.attach')}
        </Button>
      </ActionBar>
    </BrowserArea>
  );

  /* ---- Render: OneDrive — not connected ---- */
  const renderOnedriveNotConnected = () => (
    <NotConnectedArea>
      <EmptyState
        icon={
          <DriveLogoWrap>
            <img src="/logo/onedrive.png" alt="OneDrive" width={48} height={48} />
          </DriveLogoWrap>
        }
        title={t('cloudDrive.notConnected', { provider: 'OneDrive' })}
        action={
          <Button
            variant="primary"
            size="md"
            onClick={handleOneDriveConnect}
            disabled={oauthConnecting}
            isLoading={oauthConnecting}
          >
            {oauthConnecting ? t('cloudDrive.connecting') || '연결 중...' : t('cloudDrive.connect')}
          </Button>
        }
      />
    </NotConnectedArea>
  );

  /* ---- Render: loading skeleton ---- */
  const renderSkeleton = () => (
    <SkeletonArea>
      {Array.from({ length: 5 }).map((_, i) => (
        <SkeletonRow key={i}>
          <SkeletonLine width="18px" height="18px" />
          <SkeletonLine width="18px" height="18px" />
          <SkeletonLine width={`${50 + Math.random() * 30}%`} height="14px" />
          <SkeletonLine width="60px" height="12px" />
        </SkeletonRow>
      ))}
    </SkeletonArea>
  );

  /* ---- Render: OneDrive file browser ---- */
  const renderOnedriveFileBrowser = () => (
    <BrowserArea>
      <ConnectionBar>
        <ConnectionInfo>
          <ConnectionDot />
          <ConnectionLabel>OneDrive 연결됨</ConnectionLabel>
        </ConnectionInfo>
        <DisconnectBtn onClick={() => handleDisconnect('onedrive')} disabled={disconnecting}>
          <LogOut size={13} />
          <span>{disconnecting ? '해제 중...' : '연결 해제'}</span>
        </DisconnectBtn>
      </ConnectionBar>
      <SearchArea>
        <Input
          variant="search"
          inputSize="sm"
          placeholder={t('cloudDrive.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        {searchQuery && (
          <ClearSearchBtn onClick={clearSearch}><X size={14} /></ClearSearchBtn>
        )}
      </SearchArea>
      {/* Breadcrumb */}
      <BreadcrumbBar>
        {isSearchMode ? (
          <>
            <BreadcrumbBtn onClick={clearSearch}><ArrowLeft size={14} /></BreadcrumbBtn>
            <BreadcrumbText>{t('cloudDrive.searchResults')}: &ldquo;{searchQuery}&rdquo;</BreadcrumbText>
          </>
        ) : (
          <>
            {onedriveBreadcrumb.length > 1 && (
              <BreadcrumbBtn onClick={handleGoBack}><ArrowLeft size={14} /></BreadcrumbBtn>
            )}
            {onedriveBreadcrumb.map((crumb, i) => (
              <React.Fragment key={crumb.id + i}>
                {i > 0 && <BreadcrumbSep><ChevronRight size={12} /></BreadcrumbSep>}
                <BreadcrumbLink
                  $isLast={i === onedriveBreadcrumb.length - 1}
                  onClick={() => i < onedriveBreadcrumb.length - 1 && handleBreadcrumbClick(i)}
                >
                  {crumb.name}
                </BreadcrumbLink>
              </React.Fragment>
            ))}
          </>
        )}
      </BreadcrumbBar>
      <ScrollArea ref={scrollAreaRef}>
        {loading && renderSkeleton()}
        {!loading && error && (
          <EmptyState
            icon={<RefreshCw size={32} />}
            title={t('cloudDrive.errorOccurred')}
            description={error}
            action={
              <Button variant="secondary" size="sm" onClick={() => fetchFiles(onedriveBreadcrumb[onedriveBreadcrumb.length - 1].id)}>
                {t('cloudDrive.retry')}
              </Button>
            }
          />
        )}
        {!loading && !error && items.length === 0 && (
          <EmptyState
            icon={<Folder size={32} />}
            title={isSearchMode ? t('cloudDrive.noSearchResults') : t('cloudDrive.emptyFolder')}
            description={isSearchMode ? t('cloudDrive.tryDifferentKeyword') : ''}
          />
        )}
        {!loading && !error && items.length > 0 && (
          <FileListArea>
            {items.map((item) => {
              const folder = isFolder(item);
              const isSelected = selected.has(item.id);
              const modTime = item.modifiedTime || item.lastModifiedDateTime;
              const typeSelectable = isSelectableFile(item);
              const sizeSelectable = isAttachmentSizeAllowed(item.size);
              const countSelectable = isSelected || getRemainingAttachmentSlots(existingAttachmentCount, selected.size) > 0;
              const selectable = folder || (typeSelectable && sizeSelectable && countSelectable);
              const exportInfo = getExportInfo(item);

              return (
                <FileRow
                  key={item.id}
                  $isFolder={folder}
                  $isSelected={isSelected}
                  $disabled={!selectable}
                  onClick={() => (folder ? handleFolderClick(item) : toggleSelect(item))}
                >
                  {!folder && (
                    <CheckboxCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={isSelected} onChange={() => toggleSelect(item)} size="sm" disabled={!selectable} />
                    </CheckboxCell>
                  )}
                  {folder && <FolderSpacer />}
                  <FileIconWrap $isFolder={folder}>{getFileIcon(item)}</FileIconWrap>
                  <FileInfo>
                    <FileName>
                      {item.name}
                      {exportInfo && <ExportBadge>{t('cloudDrive.convertTo', { format: exportInfo.exportName })}</ExportBadge>}
                    </FileName>
                    <FileMeta>
                      {formatFileSize(item.size)}
                      {formatFileSize(item.size) && modTime ? ' · ' : ''}
                      {formatDateLocalized(modTime)}
                    </FileMeta>
                  </FileInfo>
                  {folder && <FolderChevron><ChevronRight size={14} /></FolderChevron>}
                </FileRow>
              );
            })}
          </FileListArea>
        )}
        {loadingMore && (
          <LoadMoreIndicator>
            <Loader size={16} className="spin" />
            <span>{t('cloudDrive.loadingMore')}</span>
          </LoadMoreIndicator>
        )}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </ScrollArea>
      <ActionBar>
        <SelectedCount>
          {selected.size > 0
            ? t('cloudDrive.filesSelected', { count: selected.size, total: existingAttachmentCount + selected.size, max: CHAT_ATTACHMENT_MAX_COUNT })
            : existingAttachmentCount > 0
              ? t('cloudDrive.currentAttached', { count: existingAttachmentCount, max: CHAT_ATTACHMENT_MAX_COUNT })
              : t('cloudDrive.selectFiles')}
        </SelectedCount>
        <Button variant="primary" size="sm" disabled={selected.size === 0} onClick={handleAttach}>
          {t('cloudDrive.attach')}
        </Button>
      </ActionBar>
    </BrowserArea>
  );

  /* ---- Main render ---- */
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('cloudDrive.title')}
      titleIcon={<Cloud size={18} />}
      width="600px"
      resizable
      defaultHeight={580}
      minWidth={400}
      minHeight={320}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
    >
      <ModalBody>
        <Tabs
          items={tabItems}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as DriveProvider)}
        />
        <TabContent>
          {pluginsLoading ? (
            <LoadingWrap><RingSpinner size={24} /></LoadingWrap>
          ) : activeTab === 'google_drive' ? (
            isConnected('google_drive') ? renderGdrivePicker() : renderGdriveNotConnected()
          ) : (
            isConnected('onedrive') ? renderOnedriveFileBrowser() : renderOnedriveNotConnected()
          )}
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin: 0 -24px -24px;

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotConnectedArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DriveLogoWrap = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
  }
`;

const TabIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

/* ---- Browser ---- */

const BrowserArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ConnectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  flex-shrink: 0;
`;

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ConnectionDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34c759;
  flex-shrink: 0;
`;

const ConnectionLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DisconnectBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(248,113,113,0.12)' : 'rgba(239,68,68,0.08)'};
    color: ${({ theme }) => theme.colors.error ?? '#ef4444'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SearchArea = styled.div`
  position: relative;
  padding: 12px 16px 0;
`;

const ClearSearchBtn = styled.button`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-25%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ---- Breadcrumb ---- */

const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  flex-shrink: 0;
  min-height: 32px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const BreadcrumbBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const BreadcrumbSep = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  font-size: 0.75rem;
  font-weight: ${({ $isLast }) => ($isLast ? 600 : 400)};
  color: ${({ $isLast, theme }) =>
    $isLast ? theme.colors.textPrimary : theme.colors.textMuted};
  cursor: ${({ $isLast }) => ($isLast ? 'default' : 'pointer')};
  transition: color ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;

  &:hover {
    ${({ $isLast, theme }) =>
      !$isLast &&
      css`
        color: ${theme.colors.textPrimary};
      `}
  }
`;

const BreadcrumbText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ---- File list ---- */

const LoadMoreIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  color: ${({ theme }) => theme.colors?.textSecondary ?? '#888'};
  font-size: 13px;

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0 8px;
`;

const FileListArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const FileRow = styled.div<{ $isFolder: boolean; $isSelected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease;
  user-select: none;

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
    `}

  ${({ $isSelected, $disabled, theme }) =>
    $isSelected && !$disabled &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255,149,0,0.12)'
        : 'rgba(255,149,0,0.1)'};
    `}

  &:hover {
    background: ${({ $isSelected, $disabled, theme }) =>
      $disabled
        ? 'transparent'
        : $isSelected
          ? theme.mode === 'dark'
            ? 'rgba(255,149,0,0.15)'
            : 'rgba(255,149,0,0.12)'
          : theme.mode === 'dark'
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(0,0,0,0.04)'};
  }
`;

const CheckboxCell = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const FolderSpacer = styled.div`
  width: 18px;
  flex-shrink: 0;
`;

const FileIconWrap = styled.div<{ $isFolder: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 22%;
  background: ${({ $isFolder, theme }) =>
    $isFolder
      ? theme.mode === 'dark'
        ? 'rgba(255,200,50,0.15)'
        : 'rgba(255,180,0,0.12)'
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.04)'};
  color: ${({ $isFolder, theme }) =>
    $isFolder
      ? theme.mode === 'dark'
        ? '#ffc832'
        : '#e8a000'
      : theme.colors.textMuted};
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${FileRow}:hover & {
    transform: scale(1.08) rotate(-4deg);
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const FileName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ExportBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.625rem;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 6px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(96,165,250,0.15)' : 'rgba(59,130,246,0.1)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#93bbfc' : '#2563eb'};
`;

const FileMeta = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const FolderChevron = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
`;

/* ---- Action bar ---- */

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  flex-shrink: 0;
`;

const SelectedCount = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Google Drive Picker ---- */

const PickerButtonArea = styled.div`
  padding: 16px 16px 0;
`;

const PickerButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1.5px dashed ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(66,133,244,0.5)' : 'rgba(66,133,244,0.4)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(66,133,244,0.08)' : 'rgba(66,133,244,0.06)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PickerButtonText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const PickerButtonTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
`;

const PickerButtonDesc = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PickerDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 4px;
`;

const PickerDividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const PickerDividerLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const EmptyPickerArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RemovePickedBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(248,113,113,0.15)' : 'rgba(239,68,68,0.1)'};
    color: ${({ theme }) => theme.colors.error ?? '#ef4444'};
  }
`;

/* ---- Skeleton ---- */

const SkeletonArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px;
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
`;
